import {Component, EventEmitter, Input, OnInit, Output, ViewChild, AfterViewInit, OnDestroy} from '@angular/core';
import {MatPaginator, PageEvent} from "@angular/material/paginator";
import {NgForm} from "@angular/forms";
import {Partner} from "../../../shared/interface/partner";
import {ReplaySubject} from "rxjs";
import {UserPermissions} from "../../../shared/interface/user-permissions";
import {Profile} from "../../../shared/interface/profile";
import {MatDialog} from "@angular/material/dialog";
import {FilterService} from "../../../shared/services/filter.service";
import {ActivatedRoute, Router} from "@angular/router";
import {MatSnackBar} from "@angular/material/snack-bar";
import {AuthService} from "../../../shared/services/auth.service";
import {select, Store} from "@ngrx/store";
import {StoreRootState} from "../../../shared/ngrx/reducers";
import {delay, distinctUntilChanged, takeUntil} from "rxjs/operators";
import {CountedAndPaginated} from "../../../shared/interface/response/counted-and-paginated";
import {Uuid} from "../../../shared/interface/response/uuid";
import {JobResultDialogComponent} from "../../../shared/components/job-result-dialog/job-result-dialog.component";
import {HttpErrorResponse} from "@angular/common/http";
import {ConfirmDialogComponent} from "../../../shared/components/confirm-dialog/confirm-dialog.component";
import {License} from "../../../shared/interface/license";
import {LicenseService} from "../../../shared/services/license.service";
import {LicenseAddDialogComponent} from "../license-add-dialog/license-add-dialog.component";
import {getCurrentRouteState} from "../../../shared/ngrx/selector";
import {RouterStateUrl} from "../../../shared/ngrx/route-serializer";
import {DYNAMIC_DICTIONARY_LICENSE_TYPE} from "../../../shared/services/dict.service";

@Component({
  selector: 'app-all-license-list',
  templateUrl: './all-license-list.component.html',
  styleUrls: ['./all-license-list.component.scss']
})
export class AllLicenseListComponent implements AfterViewInit, OnInit, OnDestroy {
  readonly DYNAMIC_DICTIONARY_LICENSE_TYPE = DYNAMIC_DICTIONARY_LICENSE_TYPE;

  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild('filtersForm') filtersForm: NgForm;
  @Input() disableLinks = false;
  @Input() embeddedView = false;
  @Output() changed: EventEmitter<boolean> = new EventEmitter<boolean>();

  initialFilters: any;
  partnerObject: Partner;
  displayedColumns: string[] = [
    'licenseNumber',
    'licenseType',
    'user',
    'expirationDate',
    'deviceName',
    'note',
  ];
  dataSource: License[] = [];
  totalData = 0;
  userPermissions: ReplaySubject<UserPermissions>;
  profile: Profile;
  typeIds?: number[] = null;
  sortBy = 'createdAt';
  sortOrder = 'desc';

  private destroyed$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

  constructor(
    public dialog: MatDialog,
    private filterService: FilterService,
    private activatedRoute: ActivatedRoute,
    private snack: MatSnackBar,
    private auth: AuthService,
    private store: Store<StoreRootState>,
    private router: Router,
    private licenseService: LicenseService,
  ) {
    this.userPermissions = auth.getUserPermissions();
    this.licenseService.getLicensesListSubject()
      .pipe(takeUntil(this.destroyed$))
      .subscribe((result: CountedAndPaginated<License>) => {
        this.dataSource = result.items;
        this.totalData = result.pagination.total;
      });
    this.parseFilters();
  }

  parseFilters(filters?: any): void {
    this.initialFilters = this.filterService.parseFiltersFromQueryParams({
      typeOfLicense: 'int',
      username: 'string',
      contains$licenseNumber: 'string',
      deviceName: 'string',
      gte$expirationDate: 'date',
      lte$expirationDate: 'date',
    }, filters);
  }

  ngOnInit(): void {
  }

  ngAfterViewInit(): void {
    this.paginator.page
      .pipe(
        takeUntil(this.destroyed$)
      ).subscribe((event: PageEvent) => {
      this.router.navigate([this.activatedRoute.snapshot.routeConfig.path, this.filterService.parseFiltersToQueryParams({
        page: event.pageIndex,
        pageSize: event.pageSize,
        sortBy: 'createdAt',
        sortOrder: 'desc',
        ...this.filtersForm.value
      })]);
    });

    this.store
      .pipe(
        select(getCurrentRouteState),
        delay(0),
        distinctUntilChanged((prev: RouterStateUrl, curr: RouterStateUrl) => prev.url === curr.url),
        takeUntil(this.destroyed$),
      )
      .subscribe((state: RouterStateUrl) => {
        this.parseFilters(state.params);
        this.paginator.pageIndex = state.params.page;
        this.paginator.pageSize = state.params.pageSize;
        this.sortBy = 'createdAt';
        this.sortOrder = 'desc';
        this.loadListData();
      })
    ;
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  loadListData(): void {
    this.licenseService.loadList(
      this.paginator.pageIndex,
      this.paginator.pageSize,
      this.initialFilters,
      {
        sortBy: 'createdAt',
        sortOrder: 'desc'
      }
    );
  }

  filter(): void {
    const formValues = {...this.filtersForm.value};
    for (const key in formValues) {
      if (formValues[key] === undefined) {
        delete formValues[key];
      }
    }

    if (this.embeddedView) {
      if (this.paginator.pageIndex === 0) {
        this.loadListData();
      } else {
        this.paginator.firstPage();
      }
    } else {
      this.router.navigate([this.activatedRoute.snapshot.routeConfig.path,
        this.filterService.parseFiltersToQueryParams({
          page: 0,
          pageSize: this.paginator.pageSize,
          sortBy: 'createdAt',
          sortOrder: 'desc',
          ...formValues
        })
      ]);
    }
  }

  resetFilters(): void {
    this.filtersForm.resetForm();
    this.router.navigate([this.activatedRoute.snapshot.routeConfig.path, {
      page: 0,
      pageSize: this.paginator.pageSize,
      sortBy: 'createdAt',
      sortOrder: 'desc',
    }]);
  }

  addLicense(): void {
    const dialogRef = this.dialog.open(LicenseAddDialogComponent, {
      width: '90vh',
      height: '45vh',
      maxHeight: '90%',
      panelClass: 'full-height-dialog',
    });

    dialogRef.afterClosed().subscribe(result => {
      this.loadListData();
      this.changed.emit(true);
      this.dialog.closeAll();
    });
  }

  downloadReport(): void {
    this.licenseService.downloadReport(this.buildFilters())
      .subscribe((response: Uuid) => {
        this.dialog.open(JobResultDialogComponent, {
          disableClose: true,
          width: '500px',
          data: {jobId: response.uuid, showProgress: false},
        });
      }, (error: HttpErrorResponse) => {
        this.snack.open('Przy generowaniu raportu wystąpił błąd', error.status.toString(), {duration: 2000});
      });
  }

  buildFilters(): any {
    const filters = { ...this.initialFilters };
    if (this.typeIds !== null) {
      filters.in$type = this.typeIds;
    }
    return filters;
  }

  delete(license: License): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '500px',
      data: {text: 'Czy na pewno chcesz usunąć licencję?'},
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.licenseService.delete(license.uuid)
          .subscribe(() => {
            this.snack.open('Pomyślnie usunięto licencję', '', {duration: 2000});
            this.loadListData();
          });
      }
    });
  }

}
